/*
 * Real stage data client (D-1 in the dependency list, BOARDROOM-2026-08-03-221515).
 * Talks to the tickets consumer read endpoint with the consumer-Cognito access
 * token. Endpoint base comes from env (VITE_TICKETS_API_BASE) — nothing invented:
 * until tickets ship the endpoint and the env var is set, the app reports an
 * honest 'unconfigured' state (never mock for signed-in users).
 */
import type { TicketEligibility } from './eligibility'
import { seatsForPersons, type Money, type Order, type Organiser, type Ticket, type TrEvent } from './types'

export type LiveDataState = 'unconfigured' | 'loading' | 'ready' | 'unavailable'

export interface LiveData {
  orders: Order[]
  tickets: Ticket[]
  events: TrEvent[]
}

/** Proposed D-1 response shape (adapts here if tickets ship differently). */
interface ConsumerOrdersResponse {
  orders: Array<{
    id: string
    ref: string
    eventId: string
    eventName: string | null
    organiserId?: string
    organiserName: string | null
    eventWhen?: string
    venue?: string
    purchasedAt: string
    total: Money
    tickets: Array<{
      id: string
      typeName: string | null
      price?: Money
      seatStatus?: string
      /** N places for a team ticket (Team of 2 = 2, Team of 3 = 3), from the
       *  box-office FORMAT config. Absent ⇒ a solo 1-place ticket. See the
       *  data-contract gap flagged in seatsForPersons: today's box-office/my-api
       *  order payload does NOT yet expose this or per-seat holders. */
      personsPerTicket?: number
      assigneeName?: string
      assigneeEmail?: string
      bib?: string
      waveLabel?: string
      waveTime?: string
      qrToken?: string | null
      /** R1 eligibility contract (BOARDROOM-2026-08-11-201138): tickets adds
       *  these flat on each ticket in /consumer/me/orders. gender = the
       *  settings-authoritative ticket_types.gender (M/F/BOTH); ageMin/ageMax =
       *  settings resolveEffectiveAge (Option P — NO per-ticket age column, do
       *  not expect one). Any field absent ⇒ ALLOW (R3 conservative default). */
      gender?: 'M' | 'F' | 'BOTH' | string | null
      ageMin?: number | null
      ageMax?: number | null
    }>
  }>
}

/** "2026-08-03T18:12:45Z" → "3 Aug 2026" (display); passthrough if not ISO. */
export function formatPurchasedAt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const SEAT_STATUSES = new Set(['unassigned', 'pending', 'assigned', 'checked_in'])

/** Map the R1 wire fields → a Ticket.eligibility contract, or undefined when the
 *  ticket exposes no eligibility at all (⇒ unrestricted; R3 gate allows). gender
 *  is normalized to the M/F/BOTH domain — any other/absent value ⇒ null (never a
 *  false restriction). BOARDROOM-2026-08-11-201138. */
export function eligibilityFromWire(
  gender: 'M' | 'F' | 'BOTH' | string | null | undefined,
  ageMin: number | null | undefined,
  ageMax: number | null | undefined,
): TicketEligibility | undefined {
  const g = gender === 'M' || gender === 'F' || gender === 'BOTH' ? gender : null
  const min = typeof ageMin === 'number' ? ageMin : null
  const max = typeof ageMax === 'number' ? ageMax : null
  if (g === null && min === null && max === null) return undefined
  return { gender: g, ageMin: min, ageMax: max }
}

export function ticketsApiBase(env: Record<string, string | undefined> = import.meta.env): string | null {
  return env.VITE_TICKETS_API_BASE || null
}

/** Map the wire shape into the app's types.ts contract. Pure — unit-tested. */
export function mapConsumerOrders(json: ConsumerOrdersResponse): LiveData {
  const orders: Order[] = []
  const tickets: Ticket[] = []
  const eventsById = new Map<string, TrEvent>()

  for (const o of json.orders ?? []) {
    if (!eventsById.has(o.eventId)) {
      const organiserName = o.organiserName ?? 'Organiser'
      const organiser: Organiser = {
        id: o.organiserId ?? `org-${organiserName}`,
        name: organiserName,
        initials: '',
        accentFg: 'var(--accent-bright)',
        accentBg: 'rgba(18,196,172,0.14)',
      }
      eventsById.set(o.eventId, {
        id: o.eventId,
        name: o.eventName ?? 'Your event',
        organiser,
        when: o.eventWhen ?? '',
        venue: o.venue,
        timingEnabled: true,
        teamsEnabled: false,
      })
    }
    const ticketIds: string[] = []
    for (const t of o.tickets ?? []) {
      ticketIds.push(t.id)
      // Unknown/absent box-office status degrades to 'unassigned' — NEVER
      // 'assigned' (data-integrity: an unrecognised status must not render a
      // ticket as claimed; assign-cascade fix, dispatch BOARDROOM-2026-08-11-081455).
      const seatStatus: Ticket['seatStatus'] =
        SEAT_STATUSES.has(t.seatStatus ?? '') ? (t.seatStatus as Ticket['seatStatus']) : 'unassigned'
      // Team-seat model (canon §7): a Team-of-N ticket has N places. Map
      // personsPerTicket → N seats WHERE the wire exposes it; absent ⇒ a solo
      // 1-place ticket. The (single) box-office holder lands on seat 1 for
      // display; seats 2..N are unassigned until my-api exposes per-seat
      // holders (TODO(infra seats[] contract), types.ts). The my-api holder
      // stays authoritative for allocation state (ordersModel.applyAllocations).
      tickets.push({
        id: t.id,
        orderId: o.id,
        eventId: o.eventId,
        typeName: t.typeName ?? 'Ticket',
        price: t.price ?? { amount: 0, currency: o.total.currency },
        seatStatus,
        seats: seatsForPersons(
          t.personsPerTicket ?? 1,
          seatStatus,
          t.assigneeName || t.assigneeEmail
            ? { name: t.assigneeName, email: t.assigneeEmail }
            : undefined,
        ),
        assignee: t.assigneeName
          ? { id: `p-${t.id}`, kind: 'self', name: t.assigneeName, email: t.assigneeEmail, hasLogin: true }
          : undefined,
        bib: t.bib,
        wave: t.waveLabel ? { label: t.waveLabel, time: t.waveTime ?? '' } : undefined,
        qrToken: t.qrToken ?? undefined,
        eligibility: eligibilityFromWire(t.gender, t.ageMin, t.ageMax),
      })
    }
    orders.push({
      id: o.id,
      ref: o.ref,
      eventId: o.eventId,
      purchasedAt: formatPurchasedAt(o.purchasedAt),
      total: o.total,
      ticketIds,
    })
  }
  return { orders, tickets, events: [...eventsById.values()] }
}

/** Thrown when the orders fetch is rejected for AUTH reasons (401/403) — an
 *  expired/invalid session token, NOT a backend outage. The caller drops to a
 *  re-login rather than the misleading "service not responding" empty state
 *  (BOARDROOM-2026-08-11-162146: healthy backends, the token was being
 *  rejected, and the honest state is "your session expired — sign in again"). */
export class ApiAuthError extends Error {}

export async function fetchConsumerOrders(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<LiveData> {
  // Auth per tickets' deployed contract (BOARDROOM-2026-08-03-234332):
  // Bearer <Cognito consumer ID token>, email_verified required.
  const res = await fetchImpl(`${base.replace(/\/$/, '')}/consumer/me/orders`, {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  // 401/403 = the token was rejected (expired/invalid), a DISTINCT case from a
  // backend being down — surface it typed so the UI re-authenticates instead of
  // showing "service not responding".
  if (res.status === 401 || res.status === 403) {
    throw new ApiAuthError(`consumer orders unauthorized (${res.status})`)
  }
  if (!res.ok) throw new Error(`consumer orders fetch failed (${res.status})`)
  return mapConsumerOrders((await res.json()) as ConsumerOrdersResponse)
}
